import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { ThemeProvider } from 'styled-components';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { isProjectMappingInHelpCenterEnabled } from '@helpcenter/feature-flags';
import { lightenColor, readableTextColor } from '@helpcenter/styles/src/colors';
import { GlobalStyled } from '@helpcenter/styles/src/global';
import { di } from 'react-magnetic-di';
import type { BrowserHistory } from 'react-resource-router';
import { RouteComponent, Router } from 'react-resource-router';
import { helpcenterRovoServiceAgentRoute } from 'routes/help-center-rovo';
import {
	helpcenterEditRoute,
	helpcenterManageProjectsRoute,
	helpcenterManageCustomPagesRoute,
	hubEditLayoutRoute,
	hubEditPortalLayoutRoute,
} from 'routes/help-center-settings';
import { helpcenterVirtualAgentRoute } from 'routes/help-center-virtual-agent';
import {
	viewHelpCentersManagementPageRoute,
	viewHelpHubsManagementPageRoute,
} from 'routes/help-centers-management';
import { getHelpCenterTitleColor, getThemeColor } from 'state/selectors/help-center';
import { UFOWatcher } from 'utils/ufo-watcher';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { getIsHelpHub } from '@atlassian/help-center-common-util/advanced-help-center';
import { history } from '@atlassian/help-center-common-util/history';
import { resourcesPlugin } from '../resource-plugin';
import { notFoundErrorRoute } from '../routes';

export interface Props {
	themeColor: string;
	titleColor: string;
}

export const HelpCentersManagementResourceRouter = (props: Props) => {
	di(Router);

	//Order matters here
	const routes = useMemo(() => {
		const includeRovoAgentRoute =
			(getIsHelpHub() && FeatureGates.checkGate('enable_rovo_in_help_hubs')) ||
			isFeatureEnabled('jira-service-management', 'jsm-help-center-rovo-agent');

		const includeVirtualAgentRoute = isFeatureEnabled(
			'jira-service-management',
			'jsm-help-center-virtual-agent',
		);

		return [
			...(isProjectMappingInHelpCenterEnabled()
				? [
						helpcenterEditRoute,
						helpcenterManageProjectsRoute,
						helpcenterManageCustomPagesRoute,
						...(FeatureGates.checkGate('enable-jsm-in-hubs')
							? [
									...(FeatureGates.checkGate('portal-pages-in-hub')
										? [hubEditPortalLayoutRoute]
										: []),
									hubEditLayoutRoute,
								]
							: []),
					]
				: []),
			// Feature gate: jsm_help_center_one-click_rovo_agent (Help Center) / enable_rovo_in_help_hubs (Help Hub)
			...(includeRovoAgentRoute ? [helpcenterRovoServiceAgentRoute] : []),
			...(includeVirtualAgentRoute ? [helpcenterVirtualAgentRoute] : []),
			viewHelpCentersManagementPageRoute,
			...(FeatureGates.checkGate('enable-jsm-in-hubs') ? [viewHelpHubsManagementPageRoute] : []),
			notFoundErrorRoute,
		];
	}, []);
	const { themeColor, titleColor } = props;
	const readableTextColorFromTheme = readableTextColor(themeColor);
	const lightColorFromTheme = lightenColor(themeColor);

	const theme = {
		titleColor,
		readableTextColor: readableTextColorFromTheme,
		color: themeColor,
		lightColor: lightColorFromTheme,
	};

	//isGlobal=false, to avoid the router conflicting with the sidebar's router.
	return (
		<ThemeProvider theme={theme}>
			<Router
				routes={routes}
				history={history as BrowserHistory}
				isGlobal={false}
				plugins={[resourcesPlugin]}
			>
				<GlobalStyled />
				{!__SERVER__ ? <UFOWatcher /> : null}
				<RouteComponent />
			</Router>
		</ThemeProvider>
	);
};

const selector = createSelector(
	getThemeColor,
	getHelpCenterTitleColor,
	(themeColor, titleColor) => ({
		themeColor,
		titleColor,
	}),
);

export default connect(selector)(HelpCentersManagementResourceRouter);
